import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { Fingerprint, KeyRound } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import {
  cancelCeremonies,
  isCeremonyActive,
  subscribeCeremonyActivity,
  type CeremonyOwner,
} from "@/lib/webauthn";

const ceremonyInactive = () => false;
import type { StepUpError } from "../hooks/useStepUp";
import { StaleSessionNotice } from "./StaleSessionNotice";

interface StepUpDialogProps {
  open: boolean;
  busy: boolean;
  error: StepUpError;
  proofOnWire?: boolean;
  actionNeedsDashboardSession?: boolean;
  serverDemandedProof?: boolean;
  factorListUnavailable?: boolean;
  supportsWebAuthn: boolean;
  hasPasskey: boolean;
  hasTotp: boolean;
  onPasskey: () => void;
  onTotp: (code: string) => void;
  onCancel: () => void;
  ceremonyOwner: CeremonyOwner;
  subtitle: string;
  staleSessionBody: string;
  returnTo: string;
}

export function StepUpDialog({
  open,
  busy,
  error,
  proofOnWire = false,
  actionNeedsDashboardSession = false,
  serverDemandedProof = false,
  factorListUnavailable = false,
  supportsWebAuthn,
  hasPasskey,
  hasTotp,
  onPasskey,
  onTotp,
  onCancel,
  ceremonyOwner,
  subtitle,
  staleSessionBody,
  returnTo,
}: StepUpDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const totpInputRef = useRef<HTMLInputElement>(null);
  const passkeyButtonRef = useRef<HTMLButtonElement>(null);
  const totpInputId = useId();
  const staleNoticeId = useId();
  const factorsNoteId = useId();
  const subtitleId = useId();
  const errorId = useId();
  const [passkeyRuledOut, setPasskeyRuledOut] = useState(false);
  if (error === "unavailable" && !passkeyRuledOut) setPasskeyRuledOut(true);
  const [factorListMissed, setFactorListMissed] = useState(factorListUnavailable);
  if (factorListUnavailable && !factorListMissed) setFactorListMissed(true);

  const staleSession = error === "stale-session";
  const factorsUnknown = factorListMissed || (serverDemandedProof && !hasPasskey && !hasTotp);
  const passkeyOffered =
    (hasPasskey || factorsUnknown) && supportsWebAuthn && !passkeyRuledOut && !staleSession;
  const totpOffered =
    (hasTotp || factorsUnknown || passkeyRuledOut) &&
    !(staleSession && actionNeedsDashboardSession);
  const factorsNoteShown = factorsUnknown && (passkeyOffered || totpOffered);
  const totpDescribedBy =
    [staleSession ? staleNoticeId : null, error === "unavailable" ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  useEffect(() => {
    if (staleSession && totpOffered) totpInputRef.current?.focus();
  }, [staleSession, totpOffered]);

  useEffect(() => {
    if (!open || passkeyOffered || !totpOffered) return;
    const active = document.activeElement;
    if (active === null || active === document.body) totpInputRef.current?.focus();
  }, [open, passkeyOffered, totpOffered]);

  useEffect(() => {
    if (!busy && error === "ceremony") passkeyButtonRef.current?.focus();
  }, [busy, error]);

  const [pendingFactor, setPendingFactor] = useState<"passkey" | "totp" | null>(null);
  const ceremonyActive = useSyncExternalStore(
    subscribeCeremonyActivity,
    () => isCeremonyActive(ceremonyOwner),
    ceremonyInactive,
  );
  const passkeyPending = busy && pendingFactor === "passkey";
  const ceremonyRunning = busy && ceremonyActive;
  const passkeyCeremonyRunning = passkeyPending && ceremonyActive;
  const passkeyLabel = passkeyCeremonyRunning
    ? t("account.stepUp.cancelPasskey")
    : passkeyPending
      ? t("account.stepUp.confirming")
      : t("account.stepUp.usePasskey");

  const totpPending = busy && pendingFactor === "totp";

  const errorMessage =
    error === "required"
      ? t("account.stepUp.errorRequired")
      : error === "unavailable"
        ? t("account.stepUp.errorUnavailable")
        : error === "ceremony"
          ? t("account.stepUp.errorCeremony")
          : null;

  useLayoutEffect(() => {
    if (!passkeyCeremonyRunning) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      cancelCeremonies(ceremonyOwner);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [ceremonyOwner, passkeyCeremonyRunning]);

  const dialogDescribedBy =
    [subtitleId, factorsNoteShown ? factorsNoteId : null].filter(Boolean).join(" ") || undefined;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t("account.stepUp.title")}
      describedBy={dialogDescribedBy}
    >
      <p id={subtitleId} className="text-sm text-muted">
        {subtitle}
      </p>

      {factorsNoteShown && (
        <p id={factorsNoteId} className="mt-3 text-sm text-muted">
          {supportsWebAuthn
            ? t("account.stepUp.factorsUnknown")
            : t("account.stepUp.factorsUnknownNoPasskey")}
        </p>
      )}

      {staleSession && !totpOffered && (
        <StaleSessionNotice
          returnTo={returnTo}
          body={staleSessionBody}
          className="mt-3"
          fullWidth
          autoFocus
        />
      )}

      {errorMessage && (
        <p id={errorId} role="alert" className="mt-3 text-xs text-danger">
          {errorMessage}
        </p>
      )}

      {(passkeyOffered || totpOffered || !staleSession) && (
        <div className="mt-5 flex flex-col gap-4">
          {passkeyOffered && (
            <button
              ref={passkeyButtonRef}
              type="button"
              onClick={() => {
                if (passkeyCeremonyRunning) {
                  cancelCeremonies(ceremonyOwner);
                  return;
                }
                if (busy) return;
                setPendingFactor("passkey");
                onPasskey();
              }}
              aria-disabled={(busy && !passkeyCeremonyRunning) || undefined}
              aria-busy={(passkeyPending && !ceremonyActive) || undefined}
              data-autofocus
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              <Fingerprint size={16} />
              {passkeyLabel}
            </button>
          )}

          <p
            role="status"
            className={
              ceremonyRunning
                ? passkeyOffered
                  ? "-mt-2 text-xs text-muted"
                  : "text-xs text-muted"
                : "sr-only"
            }
          >
            {ceremonyRunning
              ? t("account.stepUp.waitingPasskey")
              : busy
                ? t("account.stepUp.confirming")
                : ""}
          </p>

          {totpOffered && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (busy) return;
                const trimmed = code.trim();
                if (!trimmed) return;
                setPendingFactor("totp");
                onTotp(trimmed);
              }}
              className="flex flex-col gap-2"
            >
              <label
                className="flex items-center gap-1.5 text-xs font-medium text-muted"
                htmlFor={totpInputId}
              >
                <KeyRound size={14} />
                {t("account.stepUp.totpLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  id={totpInputId}
                  name="otp"
                  ref={totpInputRef}
                  aria-describedby={totpDescribedBy}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  readOnly={busy}
                  aria-disabled={busy || undefined}
                  maxLength={6}
                  placeholder="123456"
                  className="h-11 flex-1 rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!busy && code.trim().length === 0}
                  aria-disabled={busy || undefined}
                  aria-busy={(totpPending && !ceremonyActive) || undefined}
                  className="rounded-lg border border-border bg-surface px-4 text-sm font-semibold text-heading transition-colors hover:border-border-light disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                >
                  {totpPending ? t("account.stepUp.confirming") : t("account.stepUp.confirm")}
                </button>
              </div>
            </form>
          )}

          {staleSession && totpOffered && (
            <StaleSessionNotice
              id={staleNoticeId}
              returnTo={returnTo}
              body={t("account.stepUp.staleSessionWithTotp")}
              emphasis="secondary"
              fullWidth
            />
          )}

          {!passkeyOffered && !totpOffered && !staleSession && (
            <p className="text-sm text-muted">{t("account.stepUp.noFactor")}</p>
          )}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
        >
          {proofOnWire && !ceremonyActive ? t("common.close") : t("common.cancel")}
        </button>
      </div>
    </Dialog>
  );
}
